import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import type { DebriefResponse } from './useAssessmentApi';

/**
 * The debrief of a finished lesson run (BR-024), for the turn card. Fetches `GET /runs/{id}/debrief` once
 * when `enabled` turns true; a failure leaves the debrief empty and the learner can still go back to Today.
 */
export const useRunDebrief = (
  runId: string | null,
  enabled: boolean
): { debrief: DebriefResponse | null; loading: boolean } => {
  const [debrief, setDebrief] = useState<DebriefResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !runId) {
      setDebrief(null);
      return;
    }
    let cancelled = false;
    const abort = new AbortController();
    setLoading(true);
    apiFetch(`/runs/${runId}/debrief`, { signal: abort.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.statusText);
        const data = (await response.json()) as DebriefResponse;
        if (!cancelled) setDebrief(data);
      })
      .catch((err) => {
        if (!cancelled) console.error('[useRunDebrief] failed:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return (): void => {
      cancelled = true;
      abort.abort();
    };
  }, [runId, enabled]);

  return { debrief, loading };
};
